import styled, { css } from 'styled-components';

type FeedbackProps = {
  $error?: boolean;
};

export const MapDisclaimer = styled.div`
  position: absolute;
  right: 1rem;
  bottom: 1rem;
  z-index: 3;
  padding: 0.6rem 1rem;
  border-radius: 0.4rem;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;

  a {
    font-weight: 600;
    text-decoration: underline;
  }
`;

export const BaseMapButton = styled.button`
  ${({ theme }) => css`
    position: absolute;
    bottom: ${theme.spacings[12]};
    left: ${theme.spacings[12]};
    z-index: 3;
    min-height: 3.4rem;
    padding: ${theme.spacings[6]} ${theme.spacings[10]};
    display: inline-flex;
    align-items: center;
    gap: ${theme.spacings[6]};
    border: 1px solid ${theme.colors.gray200};
    border-radius: ${theme.radius.xs};
    background: ${theme.colors.white};
    color: ${theme.colors.indigo800};
    font-size: ${theme.font.sizes.xxs};
    font-weight: ${theme.font.weights.bold};
    box-shadow: 0 0.2rem 0.8rem rgba(36, 44, 92, 0.2);

    svg {
      flex: 0 0 auto;
      font-size: ${theme.font.sizes.md};
    }

    &:hover,
    &:focus-visible {
      background: ${theme.colors.gray100};
    }
  `}
`;

export const MapCard = styled.article`
  ${({ theme }) => css`
    min-width: 0;
    padding: ${theme.spacings[20]};
    border: 1px solid ${theme.colors.gray200};
    border-radius: ${theme.radius.md};
    background: ${theme.colors.white};
    box-shadow: 0 0.8rem 2.4rem rgba(36, 44, 92, 0.08);
  `}
`;

export const MapHeader = styled.header`
  ${({ theme }) => css`
    min-height: 5.2rem;
    margin-bottom: ${theme.spacings[12]};
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: ${theme.spacings[16]};

    h3 {
      color: ${theme.colors.indigo800};
      font-size: ${theme.font.sizes.md};
      font-weight: ${theme.font.weights.bold};
      line-height: 1.3;
    }

    p {
      margin-top: ${theme.spacings[4]};
      color: ${theme.colors.gray600};
      font-size: ${theme.font.sizes.xs};
      line-height: 1.4;
    }

    @media (max-width: 620px) {
      flex-direction: column;
    }
  `}
`;

export const SessionCount = styled.span`
  ${({ theme }) => css`
    flex: 0 0 auto;
    padding: ${theme.spacings[6]} ${theme.spacings[10]};
    border-radius: ${theme.radius.full};
    background: rgba(1, 122, 198, 0.1);
    color: ${theme.colors.blue700};
    font-size: ${theme.font.sizes.xxs};
    font-weight: ${theme.font.weights.bold};
    white-space: nowrap;
  `}
`;

export const MapFrame = styled.div`
  ${({ theme }) => css`
    position: relative;
    overflow: hidden;
    border: 1px solid ${theme.colors.gray200};
    border-radius: ${theme.radius.sm};
    background: #e7eef4;
  `}
`;

export const MapCanvas = styled.div`
  ${({ theme }) => css`
    width: 100%;
    height: 46rem;

    .ol-viewport,
    .ol-unselectable {
      -webkit-touch-callout: none;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
    }

    .ol-viewport {
      touch-action: pan-x pan-y;
    }

    .ol-control {
      position: absolute;
    }

    .ol-control button {
      width: 3.2rem;
      height: 3.2rem;
      margin: 1px;
      padding: 0;
      display: grid;
      place-items: center;
      border: 0;
      border-radius: ${theme.radius.xs};
      background: ${theme.colors.white};
      color: ${theme.colors.indigo800};
      font-size: ${theme.font.sizes.lg};
      box-shadow: 0 0.2rem 0.8rem rgba(36, 44, 92, 0.2);
    }

    .ol-control button:hover,
    .ol-control button:focus {
      background: ${theme.colors.gray100};
    }

    .ol-zoom {
      top: ${theme.spacings[12]};
      left: ${theme.spacings[12]};
      display: grid;
      gap: ${theme.spacings[4]};
      background: transparent;
    }

    .ol-attribution {
      right: ${theme.spacings[8]};
      bottom: ${theme.spacings[8]};
      max-width: calc(100% - 1.6rem);
      display: flex;
      align-items: center;
      flex-flow: row-reverse;
      background: rgba(255, 255, 255, 0.88);
      font-size: 1rem;
      text-align: right;
    }

    .ol-attribution ul {
      margin: 0;
      padding: 1px ${theme.spacings[6]};
    }

    .ol-attribution li {
      display: inline;
      list-style: none;
    }

    .ol-attribution.ol-collapsed ul {
      display: none;
    }

    .ol-attribution button {
      width: 2.4rem;
      height: 2.4rem;
      font-size: ${theme.font.sizes.sm};
      box-shadow: none;
    }

    @media (max-width: 620px) {
      height: 36rem;
    }
  `}
`;

export const MapFeedback = styled.div<FeedbackProps>`
  ${({ theme, $error }) => css`
    position: absolute;
    inset: 0;
    z-index: 2;
    padding: ${theme.spacings[24]};
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: ${theme.spacings[8]};
    background: rgba(247, 249, 252, 0.88);
    color: ${$error ? theme.colors.error : theme.colors.blueGray700};
    text-align: center;

    svg {
      font-size: 3.2rem;
    }

    strong {
      font-size: ${theme.font.sizes.sm};
    }

    span {
      max-width: 48rem;
      color: ${theme.colors.gray600};
      font-size: ${theme.font.sizes.xs};
      line-height: 1.4;
    }
  `}
`;

export const MapHint = styled.p`
  ${({ theme }) => css`
    margin-top: ${theme.spacings[10]};
    color: ${theme.colors.gray600};
    font-size: ${theme.font.sizes.xxs};
    line-height: 1.4;
  `}
`;
